import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { getCategorySEO } from '../utils/helpers/seo';
// Critical above-the-fold components
import { Breadcrumb } from '../components/common';
import { HeaderSection, SEOWrapper, FooterSection } from '../components/layout';
import { HeroSection, GeneralSection, FleetSection, ServicesSection, TestimonialsSection, HowItWorksSection, FeaturesSection, CTASection, TrustedBySection, FAQSection, LocationsSection } from '../components/sections';
import { usePageData, useBreadcrumbs } from '../utils/hooks';

import { fetchCategoryPage, fetchConfigPage } from '../utils/api/api';
import { Loader } from '../components/common';
import Custom404 from './404';

export default function CategoryPage({ initialData,categorySlug, notFound=false, error }) {
    const router = useRouter();
    const [strapiData, setStrapiData] = useState(initialData);
    const [apiError,setError] = useState(error);

    // Extract all data using custom hook
    const {
        configData,
        pageData,
        header,
        footer,
        heroSection,
        generalContent,
        fleetData,
        trustedByData,
        servicesData,
        testimonialsData,
        howItWorksData,
        featuresData,
        ctaData,
        locationsData
    } = usePageData(strapiData);

    // Generate breadcrumbs using custom hook
    const breadcrumbs = useBreadcrumbs('category', pageData, router.query);

    // Get FAQ data with fallback to default - handle multiple spellings
    const faqSource = pageData?.FAQ || pageData?.faq || pageData?.Faqs || pageData?.faqs || pageData?.FAQs;
    const faqData = {
        order: faqSource?.order || faqSource?.Order || 12,
        title: faqSource?.title || faqSource?.headline || 'FAQs',
        description: faqSource?.description || '',
        faqs: faqSource?.faqs || [],
    };

    // Create sections array with order - handle nested arrays
    const createOrderedSections = () => {
        const allSections = [];
        
        // Process GeneralSection - extract individual items
        if (generalContent) {
            const generalItems = Array.isArray(generalContent) ? generalContent : [generalContent];
            generalItems.forEach((item, index) => {
                allSections.push({
                    component: <GeneralSection data={[item]} globalIndex={index} categorySlug={categorySlug} />,
                    order: item.order || item.Order || 1 + (index * 0.1) // Add small offset for same order
                });
            });
        }
        
        // Add other sections
        if (trustedByData) {
            allSections.push({
                component: <TrustedBySection data={trustedByData} />,
                order: trustedByData.order || trustedByData.Order || 2
            });
        }
        
        if (featuresData) {
            allSections.push({
                component: <FeaturesSection data={featuresData} />,
                order: featuresData.order || featuresData.Order || 3
            });
        }
        
        if (fleetData) {
            allSections.push({
                component: <FleetSection fleetData={fleetData} />,
                order: fleetData.order || fleetData.Order || 4
            });
        }
        
        if (locationsData) {
            allSections.push({
                component: <LocationsSection data={locationsData} />,
                order: locationsData.order || locationsData.Order || 4.5
            });
        }
        
        if (servicesData) {
            allSections.push({
                component: <ServicesSection data={servicesData} />,
                order: servicesData.order || servicesData.Order || 5
            });
        }
        
        if (howItWorksData) {
            allSections.push({
                component: <HowItWorksSection data={howItWorksData} />,
                order: howItWorksData.order || howItWorksData.Order || 6
            });
        }
        
        if (testimonialsData) {
            allSections.push({
                component: <TestimonialsSection data={testimonialsData} />,
                order: testimonialsData.order || testimonialsData.Order || 7
            });
        }
        
        if (faqData) {
            allSections.push({
                component: <FAQSection data={faqData} />,
                order: faqData.order || faqData.Order || 12
            });
        }
        
        if (ctaData) {
            allSections.push({
                component: <CTASection data={ctaData} />,
                order: ctaData.order || ctaData.Order || 13
            });
        }
        
        // Sort by order
        return allSections.sort((a, b) => a.order - b.order);
    };

    const sections = createOrderedSections();


    // Loading fallback component
    const SectionLoader = () => <Loader type="section" size="medium" />;

    // Update data when route changes using initial data
    useEffect(() => {
        if (initialData && initialData.pageUrl !== strapiData?.pageUrl) {
            setStrapiData(initialData);
            setError(null); 
        }
    }, [initialData, strapiData?.pageUrl]);


    // Priority 0: Show 404 if explicitly marked as not found
    if (notFound) {
        return <Custom404 />;
    }

    // Priority 1: Show error if there's an error
    if (apiError) {
        return <Loader type="error" message="Oops! Something Went Wrong" description="Please wait a moment and try again. We'll be back up shortly!" fullPage={true} action={() => window.location.reload()} actionText="Refresh Page" />;
    }

    // Priority 2: Show loading if no data yet
    if (!initialData) {
        return <Loader type="loading" message="Loading page..." description="Please wait while we prepare your content." fullPage={true} />;
    }

    // Priority 3: Show empty state if data exists but is invalid/empty
    if (!pageData || Object.keys(pageData).length === 0) {
        return <Loader type="empty" message="No Content Available" description="This page doesn't have any content configured yet. Please check back later." fullPage={true} action={() => router.push('/')} actionText="Refresh Page" />;
    }

    // Generate SEO data for this category page
    const seoData = getCategorySEO(pageData, configData, strapiData.pageUrl);


    return (
        <>
            <SEOWrapper 
                pageData={pageData}
                configData={configData}
                pageUrl={strapiData.pageUrl}
                seoData={seoData}
            />

            {/* Critical above-the-fold components */}
            <HeaderSection data={header} pageUrl={strapiData.pageUrl} phoneNumber={pageData.EnhancedHeroSection?.phone} />
            <HeroSection data={heroSection} phoneNumber={header?.Contact} />
            <Breadcrumb breadcrumbs={breadcrumbs} />

            <div className="page-container" key={router.asPath}>
                <main className="main-content" style={{ display: 'flex', flexDirection: 'column' }}>
                    {/* All sections rendered in order */}
                    {sections.map((section, index) => (
                        <div key={index} style={{ order: section.order }}>
                            {section.component}
                        </div>
                    ))}
                </main>

                {/* Footer Section */}
                <FooterSection data={footer} />
            </div>
        </>
    );
}

// Server-side data fetching
export async function getServerSideProps({ params, req, res }) {
    try {
        const { categorySlug } = params;
        
        
        // Parallelize API calls to reduce server response time
        const [pageData, configData] = await Promise.all([
            fetchCategoryPage(categorySlug, true), // true = server-side
            fetchConfigPage(true) // true = server-side
        ]);

        // Check if data is invalid/empty (return as not found)
        if (
            !pageData?.data || 
            typeof pageData.data !== 'object' || 
            (Array.isArray(pageData.data) && pageData.data.every(item => item == null)) ||
            (Array.isArray(pageData.data) && pageData.data.length === 0)
        ) {
            return {
                props: {
                    initialData: null,
                    notFound: true,
                    error: null,
                },
            };
        }

        // Return valid data
        return {
            props: {
                initialData: {
                    configData: configData || {},
                    pageData: pageData.data[0],
                    pageUrl: `/${categorySlug}`,
                },
                notFound: false,
                categorySlug: categorySlug,
                error: null,
            },
        };
    } catch (err) {
        console.error('Error fetching data from Strapi:', err);
        
        // If it's an authorization error, redirect to 500
        if (err.message.includes('401') || err.message.includes('Unauthorized')) {
            return {
                redirect: {
                    destination: '/500',
                    permanent: false,
                },
            };
        }
        
        // Return error (with null initialData)
        return {
            props: {
                initialData: null,
                notFound: false,
                error: err.message || 'An unexpected error occurred while loading this page.',
            },
        };
    }
}

